import React, { useMemo, useState } from 'react'

import { Reference, useMutation, useQuery } from '@apollo/client'

import { DESTROY_CUSTOMER_ADDRESS, GET_CURRENT_CUSTOMER_ADDRESSES, SET_CUSTOMER_ADDRESS_AS_PRIMARY } from 'Const'
import useToggle from 'Hooks/useToggle'
import { Customer, CustomerAddress } from 'Types'
import { noty } from 'Utils'

import { QueryState } from '../../components/QueryState'

import AddAddressModal from './AddAddressModal'
import { AddressCard } from './Card'

interface Props {
  customer: Pick<Customer, 'firstName' | 'lastName'>
  googlePlacesApiKey: string
}

export const AddressesTab: React.FC<Props> = ({ customer, googlePlacesApiKey }) => {
  const [modalOpen, , openModal, closeModal] = useToggle()
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { data, loading, error } = useQuery(GET_CURRENT_CUSTOMER_ADDRESSES)
  const customerId = data?.currentCustomer?.id
  const addresses: CustomerAddress[] = useMemo(() => data?.currentCustomer?.addresses ?? [], [data])

  const [destroyAddress] = useMutation(DESTROY_CUSTOMER_ADDRESS, {
    update: (cache, { data: mutationData }) => {
      const deletedId = mutationData?.destroyCustomerAddress?.customerAddress?.id as string | undefined
      if (!deletedId || !customerId) return

      cache.evict({ id: cache.identify({ __typename: 'CustomerAddress', id: deletedId }) })
      cache.modify({
        id: cache.identify({ __typename: 'Customer', id: customerId }),
        fields: {
          addresses: (existing: readonly Reference[] = [], { readField }) =>
            existing.filter((ref) => readField('id', ref) !== deletedId),
        },
      })
      cache.gc()
    },
  })
  const [setAddressAsPrimary] = useMutation(SET_CUSTOMER_ADDRESS_AS_PRIMARY, {
    update: (cache, { data: mutationData }) => {
      const primaryId = mutationData?.setAsPrimaryCustomerAddress?.customerAddress?.id as string | undefined
      if (!primaryId || !customerId) return

      addresses.forEach((address) => {
        cache.modify({
          id: cache.identify({ __typename: 'CustomerAddress', id: address.id }),
          fields: { primary: () => address.id === primaryId },
        })
      })
      cache.modify({
        id: cache.identify({ __typename: 'Customer', id: customerId }),
        fields: {
          primaryAddress: (_, { toReference }) => toReference({ __typename: 'CustomerAddress', id: primaryId }),
        },
      })
    },
  })

  const handleDelete = async (id: string) => {
    setDeletingId(id)
    try {
      await destroyAddress({ variables: { input: { id } } })
    } catch (err) {
      noty(err instanceof Error ? err.message : 'Unable to delete this address', 'error')
    } finally {
      setDeletingId(null)
    }
  }

  const handleSetPrimary = async (id: string) => {
    try {
      await setAddressAsPrimary({ variables: { input: { id } } })
    } catch (err) {
      noty(err instanceof Error ? err.message : 'Unable to update this address', 'error')
    }
  }

  return (
    <div className="user-profile-panel user-profile-panel_enter">
      <section className="user-profile-section">
        <h2 className="user-profile-section__title">Delivery Addresses</h2>
        <QueryState
          emptyLabel="No saved addresses"
          error={error}
          isEmpty={!loading && !error && addresses.length === 0}
          loading={loading}
        >
          <div className="user-profile-address-list">
            {addresses.map((address) => (
              <AddressCard
                key={address.id}
                address={address}
                deleting={deletingId === address.id}
                onDelete={(id) => void handleDelete(id)}
                onSetPrimary={(id) => void handleSetPrimary(id)}
              />
            ))}
          </div>
        </QueryState>

        <button className="btn btn-accent" type="button" onClick={openModal}>
          Add New Address
        </button>
      </section>

      {modalOpen && customerId && (
        <AddAddressModal
          closeModal={closeModal}
          customer={customer}
          customerId={customerId}
          googlePlacesApiKey={googlePlacesApiKey}
        />
      )}
    </div>
  )
}
